// src/screens/dashboard/DashboardScreen.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "../../firebase";
import Sidebar from "../../components/dashboard/Sidebar";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";

/* ---- Data ---- */
const applicationsByMonth = [
  { month: "Jun", applied: 4, shortlisted: 2, rejected: 1 },
  { month: "Jul", applied: 7, shortlisted: 4, rejected: 2 },
  { month: "Aug", applied: 12, shortlisted: 7, rejected: 3 },
  { month: "Sep", applied: 9, shortlisted: 5, rejected: 2 },
  { month: "Oct", applied: 15, shortlisted: 9, rejected: 4 },
];

const activityData = [
  { day: "Mon", views: 14, applications: 3 },
  { day: "Tue", views: 22, applications: 5 },
  { day: "Wed", views: 18, applications: 2 },
  { day: "Thu", views: 30, applications: 8 },
  { day: "Fri", views: 25, applications: 6 },
  { day: "Sat", views: 10, applications: 1 },
  { day: "Sun", views: 8,  applications: 2 },
];

const statusData = [
  { name: "Applied",     value: 47, color: "#8B5CF6" },
  { name: "Shortlisted", value: 27, color: "#A78BFA" },
  { name: "Interview",   value: 14, color: "#C4B5FD" },
  { name: "Accepted",    value: 8,  color: "#6D28D9" },
  { name: "Rejected",    value: 12, color: "#EDE9FE" },
];

const recentApplications = [
  { company: "Google India", role: "ML Intern", status: "Shortlisted", date: "Oct 02", statusColor: "#7C3AED", statusBg: "#EDE9FE" },
  { company: "Microsoft",    role: "SWE Intern", status: "Applied",     date: "Oct 01", statusColor: "#5B21B6", statusBg: "#F5F3FF" },
  { company: "Infosys",      role: "Data Analyst", status: "Interview", date: "Sep 30", statusColor: "#6D28D9", statusBg: "#EDE9FE" },
  { company: "Wipro",        role: "Web Dev Intern", status: "Rejected", date: "Sep 28", statusColor: "#DC2626", statusBg: "#FEF2F2" },
  { company: "Flipkart",     role: "Product Intern", status: "Applied",  date: "Sep 27", statusColor: "#5B21B6", statusBg: "#F5F3FF" },
];

const upcomingInterviews = [
  { company: "Google India", time: "Tomorrow, 10:00 AM", type: "Technical", avatar: "G" },
  { company: "Infosys",      time: "Oct 6, 2:30 PM",    type: "HR Round",  avatar: "I" },
];

/* ---- Stat Card ---- */
const StatCard = ({ label, value, sub, icon, gradient, delay = "0s" }) => (
  <div
    style={{
      borderRadius: 20,
      padding: "22px 24px",
      background: "rgba(255,255,255,0.82)",
      backdropFilter: "blur(16px)",
      border: "1.5px solid rgba(196,181,253,0.35)",
      boxShadow: "0 8px 32px rgba(109,40,217,0.08), 0 1px 4px rgba(255,255,255,0.8) inset",
      display: "flex", alignItems: "flex-start", gap: 16,
      animation: "fadeInUp 0.5s ease both",
      animationDelay: delay,
      flex: 1,
      minWidth: 0,
    }}
  >
    <div style={{
      width: 52, height: 52, borderRadius: 16, flexShrink: 0,
      background: gradient,
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 4px 14px rgba(109,40,217,0.22)",
    }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 28, fontFamily: "Outfit,sans-serif", fontWeight: 800, color: "#4C1D95", lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#7C3AED", marginTop: 2 }}>{label}</div>
      <div style={{ fontSize: 11, color: "#A78BFA", marginTop: 4, fontWeight: 500 }}>{sub}</div>
    </div>
  </div>
);

/* ---- Custom Tooltip ---- */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:"rgba(255,255,255,0.95)", border:"1.5px solid rgba(196,181,253,0.5)", borderRadius:12, padding:"10px 14px", boxShadow:"0 8px 24px rgba(109,40,217,0.14)" }}>
      <p style={{ fontWeight:700, fontSize:12, color:"#5B21B6", marginBottom:4 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ fontSize:12, color:p.color, fontWeight:600, margin:"2px 0" }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

/* ---- Main Dashboard ---- */
const DashboardScreen = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState("week");
  const [appsCount, setAppsCount] = useState(0);
  const [recentApps, setRecentApps] = useState([]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { navigate("/login"); return; }
      setUser(u);
      try {
        const snap = await getDoc(doc(db, "internshipusers", u.uid));
        if (snap.exists()) setUserData(snap.data());
        const q = query(collection(db, "internshipapplications"), where("userId", "==", u.uid));
        const appsSnap = await getDocs(q);
        setAppsCount(appsSnap.size);
        
        const fetchedApps = [];
        appsSnap.forEach(doc => {
          const data = doc.data();
          if (data.paymentStatus === "Paid" || data.paymentStatus === "Installment 1 Paid") {
            let appDate = "Recent";
            if (data.appliedAt && data.appliedAt.seconds) {
              const d = new Date(data.appliedAt.seconds * 1000);
              appDate = d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
            }
            fetchedApps.push({
              company: "QuBright Internship",
              role: data.internshipTitle || "Intern",
              date: appDate,
              status: "Paid",
              statusBg: "#D1FAE5",
              statusColor: "#065F46"
            });
          }
        });
        setRecentApps(fetchedApps);
      } catch (e) { console.error("Fetch user error:", e); }
      setLoading(false);
    });
    return () => unsub();
  }, [navigate]);

  if (loading) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 60%, #DDD6FE 100%)" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:52, height:52, borderRadius:"50%", border:"4px solid #EDE9FE", borderTopColor:"#7C3AED", animation:"spin 0.8s linear infinite", margin:"0 auto 16px" }} />
        <p style={{ color:"#7C3AED", fontWeight:600, fontFamily:"Outfit,sans-serif" }}>Loading your dashboard...</p>
      </div>
    </div>
  );

  const displayName = userData ? `${userData.firstName} ${userData.lastName}` : user?.email?.split("@")[0] || "Student";

  return (
    <div style={{ display:"flex", minHeight:"100vh", background:"linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 50%, #F5F3FF 100%)", fontFamily:"Inter,sans-serif" }}>
      <style>{`
        @keyframes fadeInUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin { to { transform:rotate(360deg); } }
        @keyframes float { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-8px); } }
        ::-webkit-scrollbar { width:6px; } ::-webkit-scrollbar-track { background:transparent; } ::-webkit-scrollbar-thumb { background:rgba(139,92,246,0.3); border-radius:3px; }
        .dash-main { padding: 28px 28px 28px 24px; }
        .dash-header { flex-direction: row; }
        .dash-search { display: flex; }
        .dash-header-right { display: flex; }
        .dash-grid-2 { grid-template-columns: 1fr 1fr; }
        .dash-grid-area { grid-template-columns: 1.5fr 1fr; }
        .dash-stat-cards { flex-direction: row; }
        .dash-quick-links { flex-direction: row; }
        @media (max-width: 768px) {
          .dash-main { padding: 72px 16px 32px 16px !important; }
          .dash-header { flex-direction: column !important; gap: 12px !important; }
          .dash-search { display: none !important; }
          .dash-header-right { gap: 8px !important; }
          .dash-grid-2 { grid-template-columns: 1fr !important; }
          .dash-grid-area { grid-template-columns: 1fr !important; }
          .dash-stat-cards { flex-direction: column !important; }
          .dash-quick-links { flex-direction: column !important; }
        }
        @media (max-width: 480px) {
          .dash-main { padding: 68px 12px 28px 12px !important; }
        }
      `}</style>

      {/* Sidebar */}
      <Sidebar user={user} userData={userData} collapsed={collapsed} setCollapsed={setCollapsed} />

      {/* Main content */}
      <main className="dash-main" style={{ flex:1, overflowY:"auto", minWidth:0 }}>

        {/* ---- Header ---- */}
        <div className="dash-header" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:28, animation:"fadeInUp 0.4s ease both", gap:16 }}>
          <div>
            <h1 style={{ fontSize:26, fontFamily:"Outfit,sans-serif", fontWeight:800, color:"#4C1D95", margin:0, lineHeight:1.2 }}>
              Good {new Date().getHours() < 12 ? "Morning" : new Date().getHours() < 17 ? "Afternoon" : "Evening"}, {userData?.firstName || "Student"}
            </h1>
            <p style={{ color:"#8B5CF6", fontSize:13, fontWeight:500, marginTop:4 }}>
              Here is what is happening with your internship journey today.
            </p>
          </div>

          <div className="dash-header-right" style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div className="dash-search" style={{
              display:"flex", alignItems:"center", gap:8,
              background:"rgba(255,255,255,0.75)", backdropFilter:"blur(12px)",
              border:"1.5px solid rgba(196,181,253,0.4)", borderRadius:14,
              padding:"8px 14px",
            }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#A78BFA" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input placeholder="Search internships..." style={{ border:"none", background:"transparent", outline:"none", fontSize:13, color:"#5B21B6", fontFamily:"Inter,sans-serif", width:160 }} />
            </div>

            {/* Notification */}
            <div style={{ position:"relative" }}>
              <button style={{ width:40, height:40, borderRadius:12, background:"rgba(255,255,255,0.75)", border:"1.5px solid rgba(196,181,253,0.4)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", backdropFilter:"blur(12px)" }}>
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#7C3AED" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </button>
              <div style={{ position:"absolute", top:-2, right:-2, width:12, height:12, background:"#7C3AED", borderRadius:"50%", border:"2px solid white", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <span style={{ color:"#fff", fontSize:7, fontWeight:700 }}>3</span>
              </div>
            </div>

            {/* Avatar */}
            <div style={{ width:40, height:40, borderRadius:12, background:"linear-gradient(135deg, #7C3AED, #A78BFA)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:15, fontFamily:"Outfit,sans-serif", cursor:"pointer", boxShadow:"0 4px 12px rgba(124,58,237,0.3)" }}>
              {userData?.firstName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "U"}
            </div>
          </div>
        </div>

        {/* ---- Stat Cards ---- */}
        <div className="dash-stat-cards" style={{ display:"flex", gap:16, marginBottom:24, flexWrap:"wrap" }}>
          <StatCard label="Total Applications" value={appsCount.toString()} sub="Your active apps" delay="0.05s"
            gradient="linear-gradient(135deg, #7C3AED, #8B5CF6)"
            icon={<svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
          />
          <StatCard label="Shortlisted" value="0" sub="57% success rate" delay="0.1s"
            gradient="linear-gradient(135deg, #6D28D9, #7C3AED)"
            icon={<svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
          />
          <StatCard label="Interviews" value="0" sub="3 upcoming" delay="0.15s"
            gradient="linear-gradient(135deg, #5B21B6, #6D28D9)"
            icon={<svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" /></svg>}
          />
          <StatCard label="Accepted Offers" value="0" sub="+2 this week" delay="0.2s"
            gradient="linear-gradient(135deg, #4C1D95, #5B21B6)"
            icon={<svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>}
          />
        </div>

        <div style={{ marginBottom: 24, animation: "fadeInUp 0.5s 0.22s ease both" }}>
          <div className="dash-quick-links" style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <div
              onClick={() => navigate("/dashboard/internships")}
              style={{
                flex: 1, minWidth: 220, cursor: "pointer",
                borderRadius: 20, padding: "20px 24px",
                background: "linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)",
                boxShadow: "0 8px 28px rgba(124,58,237,0.35)",
                display: "flex", alignItems: "center", gap: 16,
                transition: "transform 0.25s, box-shadow 0.25s",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 14px 36px rgba(124,58,237,0.45)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(124,58,237,0.35)"; }}
            >
              <div style={{ width: 52, height: 52, borderRadius: 16, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>🎓</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: "#fff", fontFamily: "Outfit,sans-serif", fontWeight: 800, fontSize: 16 }}>Browse Internships</div>
                <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, fontWeight: 500, marginTop: 3 }}>3 programs available — Full Stack, AI&ML, Cyber Security</div>
              </div>
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="rgba(255,255,255,0.9)" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>
            <div
              onClick={() => navigate("/dashboard/my-applications")}
              style={{
                flex: 1, minWidth: 220, cursor: "pointer",
                borderRadius: 20, padding: "20px 24px",
                background: "rgba(255,255,255,0.82)", backdropFilter: "blur(16px)",
                border: "1.5px solid rgba(196,181,253,0.45)",
                boxShadow: "0 8px 28px rgba(109,40,217,0.09)",
                display: "flex", alignItems: "center", gap: 16,
                transition: "transform 0.25s, box-shadow 0.25s",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 14px 36px rgba(109,40,217,0.15)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(109,40,217,0.09)"; }}
            >
              <div style={{ width: 52, height: 52, borderRadius: 16, background: "linear-gradient(135deg,#EDE9FE,#DDD6FE)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>📋</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: "#4C1D95", fontFamily: "Outfit,sans-serif", fontWeight: 800, fontSize: 16 }}>My Applications</div>
                <div style={{ color: "#8B5CF6", fontSize: 12, fontWeight: 500, marginTop: 3 }}>Track status & complete payment for applied internships</div>
              </div>
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#7C3AED" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>
          </div>
        </div>

        <div className="dash-grid-2" style={{ display:"grid", gap:20, marginBottom:20 }}>

          {/* Applications bar chart */}
          <div style={{ borderRadius:20, padding:"22px 20px", background:"rgba(255,255,255,0.82)", backdropFilter:"blur(16px)", border:"1.5px solid rgba(196,181,253,0.35)", boxShadow:"0 8px 32px rgba(109,40,217,0.07)", animation:"fadeInUp 0.5s 0.25s ease both" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
              <div>
                <h3 style={{ margin:0, fontSize:15, fontFamily:"Outfit,sans-serif", fontWeight:700, color:"#4C1D95" }}>Application Activity</h3>
                <p style={{ margin:0, fontSize:11, color:"#A78BFA", fontWeight:500, marginTop:2 }}>Monthly breakdown</p>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                {["week","month"].map(t => (
                  <button key={t} onClick={() => setActiveTab(t)} style={{ padding:"4px 12px", borderRadius:8, border:"none", cursor:"pointer", background: activeTab===t ? "#7C3AED" : "rgba(237,233,254,0.8)", color: activeTab===t ? "#fff" : "#8B5CF6", fontSize:11, fontWeight:600, transition:"all 0.2s" }}>
                    {t.charAt(0).toUpperCase()+t.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={applicationsByMonth} barSize={10} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(196,181,253,0.3)" vertical={false} />
                <XAxis dataKey="month" tick={{ fill:"#A78BFA", fontSize:11, fontFamily:"Inter" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill:"#A78BFA", fontSize:11, fontFamily:"Inter" }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="applied" name="Applied" fill="#7C3AED" radius={[6,6,0,0]} />
                <Bar dataKey="shortlisted" name="Shortlisted" fill="#A78BFA" radius={[6,6,0,0]} />
                <Bar dataKey="rejected" name="Rejected" fill="#DDD6FE" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Status Pie chart */}
          <div style={{ borderRadius:20, padding:"22px 20px", background:"rgba(255,255,255,0.82)", backdropFilter:"blur(16px)", border:"1.5px solid rgba(196,181,253,0.35)", boxShadow:"0 8px 32px rgba(109,40,217,0.07)", animation:"fadeInUp 0.5s 0.3s ease both" }}>
            <div style={{ marginBottom:18 }}>
              <h3 style={{ margin:0, fontSize:15, fontFamily:"Outfit,sans-serif", fontWeight:700, color:"#4C1D95" }}>Application Status</h3>
              <p style={{ margin:0, fontSize:11, color:"#A78BFA", fontWeight:500, marginTop:2 }}>Total 108 applications</p>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <ResponsiveContainer width={160} height={160}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={45} outerRadius={72} dataKey="value" strokeWidth={0}>
                    {statusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ flex:1, display:"flex", flexDirection:"column", gap:8 }}>
                {statusData.map((d, i) => (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{ width:10, height:10, borderRadius:3, background:d.color, flexShrink:0 }} />
                    <span style={{ fontSize:12, color:"#5B21B6", fontWeight:600, flex:1 }}>{d.name}</span>
                    <span style={{ fontSize:12, color:"#7C3AED", fontWeight:700 }}>{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ---- Area chart + Upcoming interviews ---- */}
        <div className="dash-grid-area" style={{ display:"grid", gap:20, marginBottom:20 }}>

          {/* Weekly activity area chart */}
          <div style={{ borderRadius:20, padding:"22px 20px", background:"rgba(255,255,255,0.82)", backdropFilter:"blur(16px)", border:"1.5px solid rgba(196,181,253,0.35)", boxShadow:"0 8px 32px rgba(109,40,217,0.07)", animation:"fadeInUp 0.5s 0.35s ease both" }}>
            <div style={{ marginBottom:18 }}>
              <h3 style={{ margin:0, fontSize:15, fontFamily:"Outfit,sans-serif", fontWeight:700, color:"#4C1D95" }}>Weekly Activity</h3>
              <p style={{ margin:0, fontSize:11, color:"#A78BFA", fontWeight:500, marginTop:2 }}>Profile views vs applications</p>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={activityData}>
                <defs>
                  <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#7C3AED" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="appsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#A78BFA" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#A78BFA" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(196,181,253,0.3)" vertical={false} />
                <XAxis dataKey="day" tick={{ fill:"#A78BFA", fontSize:11, fontFamily:"Inter" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill:"#A78BFA", fontSize:11, fontFamily:"Inter" }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="views" name="Profile Views" stroke="#7C3AED" strokeWidth={2} fill="url(#viewsGrad)" dot={false} />
                <Area type="monotone" dataKey="applications" name="Applications" stroke="#A78BFA" strokeWidth={2} fill="url(#appsGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Upcoming interviews */}
          <div style={{ borderRadius:20, padding:"22px 20px", background:"rgba(255,255,255,0.82)", backdropFilter:"blur(16px)", border:"1.5px solid rgba(196,181,253,0.35)", boxShadow:"0 8px 32px rgba(109,40,217,0.07)", animation:"fadeInUp 0.5s 0.4s ease both" }}>
            <div style={{ marginBottom:16 }}>
              <h3 style={{ margin:0, fontSize:15, fontFamily:"Outfit,sans-serif", fontWeight:700, color:"#4C1D95" }}>Upcoming Interviews</h3>
              <p style={{ margin:0, fontSize:11, color:"#A78BFA", fontWeight:500, marginTop:2 }}>Schedule overview</p>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {upcomingInterviews.map((iv, i) => (
                <div key={i} style={{ padding:"14px 16px", borderRadius:14, background:"linear-gradient(135deg, rgba(237,233,254,0.8), rgba(245,243,255,0.6))", border:"1px solid rgba(196,181,253,0.4)", display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ width:40, height:40, borderRadius:12, background:"linear-gradient(135deg, #7C3AED, #A78BFA)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:16, flexShrink:0 }}>
                    {iv.avatar}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#4C1D95", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{iv.company}</div>
                    <div style={{ fontSize:11, color:"#7C3AED", fontWeight:600, marginTop:1 }}>{iv.type}</div>
                    <div style={{ fontSize:10, color:"#A78BFA", fontWeight:500, marginTop:2 }}>{iv.time}</div>
                  </div>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:"#7C3AED", animation:"float 2s ease-in-out infinite", animationDelay:`${i*0.5}s` }} />
                </div>
              ))}
              <button style={{ padding:"11px 16px", borderRadius:14, border:"1.5px dashed rgba(167,139,250,0.5)", background:"transparent", color:"#8B5CF6", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"Inter,sans-serif", transition:"all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.background="rgba(237,233,254,0.5)"; }}
                onMouseLeave={e => { e.currentTarget.style.background="transparent"; }}
              >
                + View Full Schedule
              </button>
            </div>
          </div>
        </div>

        {/* ---- Recent Applications Table ---- */}
        <div style={{ borderRadius:20, padding:"22px 24px", background:"rgba(255,255,255,0.82)", backdropFilter:"blur(16px)", border:"1.5px solid rgba(196,181,253,0.35)", boxShadow:"0 8px 32px rgba(109,40,217,0.07)", animation:"fadeInUp 0.5s 0.45s ease both" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
            <div>
              <h3 style={{ margin:0, fontSize:15, fontFamily:"Outfit,sans-serif", fontWeight:700, color:"#4C1D95" }}>Recent Applications</h3>
              <p style={{ margin:0, fontSize:11, color:"#A78BFA", fontWeight:500, marginTop:2 }}>Your latest activity</p>
            </div>
            <button style={{ padding:"7px 16px", borderRadius:10, border:"1.5px solid rgba(167,139,250,0.5)", background:"rgba(237,233,254,0.6)", color:"#7C3AED", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"Inter,sans-serif" }}>
              View All
            </button>
          </div>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"separate", borderSpacing:"0 6px" }}>
              <thead>
                <tr>
                  {["Company","Role","Applied On","Status","Action"].map(h => (
                    <th key={h} style={{ textAlign:"left", fontSize:11, fontWeight:700, color:"#A78BFA", letterSpacing:"0.06em", textTransform:"uppercase", padding:"4px 12px", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentApps.length > 0 ? recentApps.map((a, i) => (
                  <tr key={i} style={{ animation:`fadeInUp 0.4s ${0.5 + i*0.06}s ease both` }}>
                    <td style={{ padding:"12px", background:"rgba(245,243,255,0.6)", borderRadius:"14px 0 0 14px", borderLeft:"2px solid rgba(196,181,253,0.3)", fontWeight:700, fontSize:13, color:"#4C1D95" }}>{a.company}</td>
                    <td style={{ padding:"12px", background:"rgba(245,243,255,0.6)", fontSize:13, color:"#5B21B6", fontWeight:500 }}>{a.role}</td>
                    <td style={{ padding:"12px", background:"rgba(245,243,255,0.6)", fontSize:12, color:"#A78BFA", fontWeight:500 }}>{a.date}</td>
                    <td style={{ padding:"12px", background:"rgba(245,243,255,0.6)" }}>
                      <span style={{ background:a.statusBg, color:a.statusColor, borderRadius:8, fontSize:11, fontWeight:700, padding:"3px 10px" }}>{a.status}</span>
                    </td>
                    <td style={{ padding:"12px", background:"rgba(245,243,255,0.6)", borderRadius:"0 14px 14px 0" }}>
                      <button style={{ background:"rgba(196,181,253,0.3)", border:"none", borderRadius:8, padding:"5px 12px", fontSize:11, color:"#5B21B6", fontWeight:600, cursor:"pointer", transition:"all 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.background="rgba(124,58,237,0.2)"}
                        onMouseLeave={e => e.currentTarget.style.background="rgba(196,181,253,0.3)"}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                  )) : <tr><td colSpan="5" style={{padding:"24px", textAlign:"center", color:"#8B5CF6", fontWeight:600}}>No paid applications found yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

      </main>
    </div>
  );
};

export default DashboardScreen;

